import React from 'react';
import {
  DecoratorNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
} from 'lexical';
import { HelpCircleFilled, Users } from 'icons/untitledui';

type MentionPayload = {
  id: string;
  label: string;
  profileId?: string;
  entityExtractionId?: string;
};

export class MentionNode extends DecoratorNode<JSX.Element> {
  private idValue: string;

  private labelValue: string;

  private profileIdValue?: string;

  private entityExtractionIdValue?: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      {
        id: node.idValue,
        label: node.labelValue,
        profileId: node.profileIdValue,
        entityExtractionId: node.entityExtractionIdValue,
      },
      node.getKey()
    );
  }

  static importJSON(serialized: any): MentionNode {
    return new MentionNode(
      {
        id: serialized.id,
        label: serialized.label,
        profileId: serialized.profileId,
        entityExtractionId: serialized.entityExtractionId,
      },
      undefined
    );
  }

  constructor(
    { id, label, profileId, entityExtractionId }: MentionPayload,
    key?: NodeKey
  ) {
    super(key);
    this.idValue = id;
    this.labelValue = label;
    this.profileIdValue = profileId;
    this.entityExtractionIdValue = entityExtractionId;
  }

  exportJSON(): any {
    return {
      type: 'mention',
      version: 1,
      id: this.idValue,
      label: this.labelValue,
      profileId: this.profileIdValue,
      entityExtractionId: this.entityExtractionIdValue,
    };
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'mention-chip';
    span.contentEditable = 'false';
    span.dataset.mentionId = this.idValue;
    span.dataset.mentionLabel = this.labelValue;
    span.dataset.mentionKey = this.getKey();
    span.dataset.hasProfile = this.profileIdValue ? 'true' : 'false';
    if (this.entityExtractionIdValue) {
      span.dataset.entityExtractionId = this.entityExtractionIdValue;
    }
    span.style.cursor = 'pointer';
    return span;
  }

  /* eslint-disable class-methods-use-this, @typescript-eslint/no-unused-vars */
  updateDOM(
    _prevNode: MentionNode,
    _dom: HTMLElement,
    _config: EditorConfig
  ): boolean {
    return false;
  }
  /* eslint-enable class-methods-use-this, @typescript-eslint/no-unused-vars */

  getTextContent(): string {
    return this.labelValue;
  }

  getLabel(): string {
    return this.labelValue;
  }

  getEntityExtractionId(): string | undefined {
    return this.entityExtractionIdValue;
  }

  decorate(): JSX.Element {
    const hasProfile = !!this.profileIdValue;
    const iconStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      verticalAlign: 'middle' as const,
    };

    return (
      <span>
        {hasProfile ? (
          <span style={iconStyle} className="mention-icon mention-icon-user">
            <Users.User02 size={20} />
          </span>
        ) : (
          <span style={iconStyle} className="mention-icon">
            <HelpCircleFilled size={20} />
          </span>
        )}
        {this.labelValue}
      </span>
    );
  }
}

export function $createMentionNode(payload: MentionPayload): MentionNode {
  return new MentionNode(payload);
}

export function $isMentionNode(
  node: LexicalNode | null | undefined
): node is MentionNode {
  return node instanceof MentionNode;
}
